import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { MenuItem } from 'primeng/api';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { LanguageModel } from 'projects/core/src/lib/models/language.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { TodoModel } from 'projects/core/src/lib/models/todo.model';
import { v4 as uuidv4 } from 'uuid';

class CustomTodoModel extends TodoModel {
  controls: { [key: string]: FormControl } = {};
  uuid?: string;
}

interface TodoPayload {
  id?: number | null;
  name: { [key: string]: string };
  onsite: boolean;
  virtual: boolean;
}

@Component({
  selector: 'app-template-todos',
  templateUrl: './template-todos.component.html',
  styleUrl: './template-todos.component.scss'
})
export class TemplateTodosComponent extends BaseForm implements OnInit, OnChanges {

  @Input() template: TemplateModel = null;
  @Input() languages: Array<LanguageModel> = [];
  @Output() templateChange = new EventEmitter<TemplateModel>();
  @Output() setEdit = new EventEmitter<string>();

  private tempOrder: Array<number> = [];
  public todos: Array<CustomTodoModel> = [];
  public items: Array<MenuItem> = [];
  public inOrdering = false;
  public inheritVisible = false;

  constructor(
    private fb: FormBuilder,
    private auth: AuthStateService,
    private api: ApiService,
    private transloco: TranslocoService,
    private ui: UiService
  ) {
    super();
  }

  ngOnInit(): void {
    this.form = this.fb.group({});
    this.initTodos();
  }

  ngOnChanges(): void {
    if (this.form) {
      this.initTodos();
    }
  }

  public onEdit(): void {
    if (this.auth.hasRight('admin.template.edit')) {
      this.startEdit();
      this.setEdit.emit('todos');
      if (this.todos.length === 0) {
        this.addTodo();
      }
    }
  }

  public onCancel(): void {
    this.initTodos();
    this.stopEdit();
  }

  public stopEdit(): void {
    this.inEdit = false;
    this.inOrdering = false;
    this.setEdit.emit(null);
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.inEdit === true) {
      if (this.form.valid) {
        if (this.template?.shouldShowInheritDialog()) {
          this.inheritVisible = true
        } else {
          this.saveTodos(false);
        }
      } else {
        FormHelper.markFormAsDirty(this.form);
      }
    }
    if (this.inOrdering === true) {
      this.saveOrder();
    }
  }

  private saveTodos(inherit: boolean = false): void {
    this.submitting = true;
    const payload = {
      todos: this.getPayload(),
      inherit
    };
    this.api.saveTemplateTodos(this.template.id, payload).subscribe({
      next: (result: any) => {
        this.ui.hideMessage();
        this.ui.toastSuccess(this.transloco.translate('template_todos.todos_saved'), this.transloco.translate('common.saved'));
        this.templateChange.emit(new TemplateModel().deserialize(result.data));
        this.stopEdit();
      },
      error: (error: any) => {
        this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
      }
    }).add(() => {
      this.submitting = false;
    });
  }

  private saveOrder(): void {
    this.submitting = true;
    const payload = {
      order: this.tempOrder
    };
    this.api.saveTemplateTodosOrder(this.template.id, payload).subscribe({
      next: (result: any) => {
        this.tempOrder = [];
        this.ui.hideMessage();
        this.ui.toastSuccess(this.transloco.translate('template_todos.sequence_saved'), this.transloco.translate('common.saved'));
        this.templateChange.emit(new TemplateModel().deserialize(result.data));
        this.stopEdit();
      },
      error: (error: any) => {
        this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
      }
    }).add(() => {
      this.submitting = false;
    });
  }

  public addTodo(): void {
    this.todos.push(this.getCustomTodo());
  }

  public deleteTodo(todo: CustomTodoModel): void {
    this.todos = this.todos.filter((item: CustomTodoModel) => item !== todo);
    this.languages.forEach((language: LanguageModel) => {
      this.form.removeControl(language.key + '_' + todo.uuid);
    });
    this.form.removeControl('onsite_' + todo.uuid);
    this.form.removeControl('virtual_' + todo.uuid);
  }

  private initTodos(): void {
    this.todos = this.template?.todos?.map((todo: TodoModel) => {
      return this.getCustomTodo(todo);
    });
    // set items
    if (this.auth.hasRight('admin.template.edit') && this.todos.length > 1) {
      this.items = [{
        label: this.transloco.translate('common.labels.sequence'),
        icon: 'pi pi-list',
        command: () => {
          this.onStartOrdering();
        }
      }];
    }
  }

  private getCustomTodo(todo: TodoModel | null = null): CustomTodoModel {
    const controlId = uuidv4();
    const customTodo = new CustomTodoModel().deserialize(todo);
    customTodo.uuid = controlId;
    customTodo.controls = {
      onsite: new FormControl<boolean>(customTodo.onsite || false),
      virtual: new FormControl<boolean>(customTodo.virtual || false),
    };
    this.languages.forEach((language: LanguageModel) => {
      const control = new FormControl<string>(customTodo.name[language.key] || '', [Validators.required]);
      customTodo.controls[language.key] = control;
      this.form.addControl(language.key + '_' + controlId, control);
    });
    this.form.addControl('onsite_' + controlId, customTodo.controls.onsite);
    this.form.addControl('virtual_' + controlId, customTodo.controls.virtual);
    return customTodo;
  }

  private getPayload(): Array<TodoPayload> {
    return this.todos.map((todo: CustomTodoModel) => {
      const payload: TodoPayload = {
        id: todo.id || null,
        name: {},
        onsite: todo.controls.onsite.value,
        virtual: todo.controls.virtual.value,
      };
      this.languages.forEach((language: LanguageModel) => {
        payload.name[language.key] = todo.controls[language.key].value;
      });
      return payload;
    });
  }

  // sorting

  private onStartOrdering(): void {
    this.setEdit.emit('todos');
    this.inOrdering = true;
    this.tempOrder = this.todos.map((todo: TodoModel) => todo.id);
  }

  public onRowReorder(event: any): void {
    const ids = this.tempOrder.splice(event.dragIndex, 1);
    if (ids && ids.length > 0) {
      this.tempOrder.splice(event.dropIndex, 0, ids[0]);
    }
  }

  public onStopOrdering(): void {
    this.resetOrder();
    this.inOrdering = false;
  }

  private resetOrder(): void {
    this.todos.sort((a: any, b: any) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0));
  }


}
